const Features = () => {
  return (
    <div className="bg-gray-50 text-gray-800">
      <section className="relative bg-gradient-to-r from-black via-gray-900 to-yellow-600 text-white py-24 px-6 text-center overflow-hidden">
        <div className="relative z-10 max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-6xl font-extrabold mb-4">
            Experience the <span className="text-primary">Express Advantage</span>
          </h1>
            <p className="text-lg md:text-xl max-w-3xl mx-auto text-gray-200">
            From browsing to installation — everything we do is designed to make tire shopping
            effortless, transparent, and enjoyable.
          </p>
        </div>
      </section>

      {/* Feature Highlights */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-900">
          Our Core Features
        </h2>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-10">
          {[
            {
              title: "Wide Tire & Wheel Selection",
              desc: "Explore a massive collection of top-rated tire and wheel brands to match your driving style and vehicle needs.",
              icon: (
                <svg
                  className="w-14 h-14 text-primary mx-auto mb-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 3v18M3 12h18" />
                </svg>
              ),
            },
            {
              title: "Real-Time Availability",
              desc: "See instantly which products are in stock or ready for in-store fitting, no waiting or uncertainty.",
              icon: (
                <svg
                  className="w-14 h-14 text-primary mx-auto mb-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path d="M5 13l4 4L19 7" />
                </svg>
              ),
            },
            {
              title: "Online Appointment Booking",
              desc: "Schedule your tire fitting or wheel installation at your preferred time — fast and stress-free.",
              icon: (
                <svg
                  className="w-14 h-14 text-primary mx-auto mb-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <rect x="3" y="4" width="18" height="18" rx="2" />
                  <path d="M16 2v4M8 2v4M3 10h18" />
                </svg>
              ),
            },
            {
              title: "Transparent Pricing",
              desc: "No hidden costs — get accurate, upfront pricing on every product and service before you book.",
              icon: (
                <svg
                  className="w-14 h-14 text-primary mx-auto mb-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 1v4m0 14v4M5 12H1m22 0h-4M8 8l8 8M16 8l-8 8" />
                </svg>
              ),
            },
            {
              title: "Expert Installation",
              desc: "Our trained technicians ensure every installation is safe, balanced, and precision-perfect.",
              icon: (
                <svg
                  className="w-14 h-14 text-primary mx-auto mb-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 19l9-5-9-5-9 5 9 5z" />
                  <path d="M12 12l9-5-9-5-9 5 9 5z" />
                </svg>
              ),
            },
            {
              title: "Seamless Checkout",
              desc: "Add tires or wheels to your cart and complete your purchase with secure, simple payment options.",
              icon: (
                <svg
                  className="w-14 h-14 text-primary mx-auto mb-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path d="M3 3h18v4H3zM3 9h18v12H3zM8 13h8" />
                </svg>
              ),
            },
          ].map((feature, index) => (
            <div
              key={index}
              className="p-8 bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-shadow duration-300 text-center"
            >
              {feature.icon}
              <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                {feature.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Step-by-Step Process */}
      <section className="bg-gray-900 text-white py-20">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-12">
            How It Works — Simple & Smart
          </h2>
          <div className="grid md:grid-cols-4 gap-10">
            {[
              {
                step: "01",
                title: "Browse & Choose",
                desc: "Select from our premium range of tires and wheels designed for every make and model.",
              },
              {
                step: "02",
                title: "Check Availability",
                desc: "Know exactly what’s in stock and get instant updates when new arrivals are available.",
              },
              {
                step: "03",
                title: "Book Appointment",
                desc: "Pick a time that suits you best and we’ll get your tires ready for fitting.",
              },
              {
                step: "04",
                title: "Get Installed",
                desc: "Visit our store and enjoy professional, fast, and precise installation.",
              },
            ].map((item, i) => (
              <div key={i} className="relative">
                <div className="text-primary text-6xl font-extrabold mb-4">
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                <p className="text-gray-300 text-sm leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-900">
          What Makes Us Different
        </h2>
        <div className="grid md:grid-cols-3 gap-10">
          <div className="p-8 bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all text-center">
            <svg
              className="w-12 h-12 text-primary mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path d="M5 13l4 4L19 7" />
            </svg>
            <h3 className="text-xl font-semibold mb-2">Trusted by Drivers</h3>
            <p className="text-gray-600 text-sm">
              Thousands of satisfied customers rely on our expertise and
              reliability every year.
            </p>
          </div>
          <div className="p-8 bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all text-center">
            <svg
              className="w-12 h-12 text-primary mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <circle cx="12" cy="7" r="4" />
              <path d="M6 21v-2a4 4 0 014-4h4a4 4 0 014 4v2" />
            </svg>
            <h3 className="text-xl font-semibold mb-2">Customer-Centered</h3>
            <p className="text-gray-600 text-sm">
              We tailor every experience to ensure convenience, comfort, and
              complete satisfaction.
            </p>
          </div>
          <div className="p-8 bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all text-center">
            <svg
              className="w-12 h-12 text-primary mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path d="M12 2l3 7h7l-5.5 4.5L18 21l-6-4-6 4 1.5-7.5L2 9h7l3-7z" />
            </svg>
            <h3 className="text-xl font-semibold mb-2">Top-Tier Quality</h3>
            <p className="text-gray-600 text-sm">
              Every tire, wheel, and service meets our highest standards of
              safety and performance.
            </p>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="bg-primary py-16 text-center text-white">
        <h2 className="text-3xl font-bold mb-4">
          Ready to Upgrade Your Ride?
        </h2>
        <p className="text-lg mb-6">
          Discover tires and wheels that fit perfectly and perform flawlessly —
          online and in-store.
        </p>
        <button className="bg-white text-primary px-8 py-3 rounded-full font-semibold hover:bg-yellow-100 transition-all">
          Explore Tires & Wheels
        </button>
      </section>
    </div>
  );
};

export default Features;
